// components/TipForm.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext"; // Keep auth context for user identification
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

// Import MUI components
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Fade from "@mui/material/Fade";
import Backdrop from "@mui/material/Backdrop";

// Main TipForm component using Stripe Checkout
const TipForm = () => {
  return (
    <Container maxWidth="sm">
      <Card sx={{ boxShadow: 0 }}>
        <CardContent>
          <StripeCheckoutForm />
        </CardContent>
      </Card>
    </Container>
  );
};

// Stripe Checkout form implementation
const StripeCheckoutForm = () => {
  // State management
  const [amount, setAmount] = useState(500); // Default to $5 (in cents)
  const [message, setMessage] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Store original referrer to return to after modal close
  const [referrer, setReferrer] = useState("");

  // Get auth context to access the current user
  const { currentUser } = useAuth();

  // React Router hooks for navigation and getting URL params
  const navigate = useNavigate();
  const location = useLocation();

  // Store referrer on component mount
  useEffect(() => {
    // Get the referrer from document.referrer or from state passed in navigation
    const docReferrer = document.referrer || "";
    const locationReferrer = location.state?.from || "";

    // Use the referrer that's available and fallback to homepage if none
    setReferrer(locationReferrer || docReferrer || "/");

    console.log("Referrer set to:", locationReferrer || docReferrer || "/");
  }, [location]);

  // Predefined tip amounts
  const tipOptions = [
    { value: 500, label: "$5" },
    { value: 1000, label: "$10" },
    { value: 2000, label: "$20" },
    { value: 5000, label: "$50" },
  ];

  // Handle predefined amount selection
  const handlePredefinedAmount = (value) => {
    console.log("Selected predefined amount:", value);
    setAmount(value);
  };

  // Handle custom amount input
  const handleCustomAmountChange = (event) => {
    const value = event.target.value;
    console.log("Custom amount input change:", value);
    // Convert dollar value to cents, ensure it's a number
    const cents = Math.round(parseFloat(value) * 100);
    // Check if the value is a valid number and non-negative
    if (!isNaN(cents) && cents >= 0) {
      setAmount(cents);
    } else if (value === "") {
      setAmount(0);
    }
  };

  // Reset form after payment
  const resetPaymentForm = () => {
    setAmount(500);
    setMessage("");
    setCustomerName("");
    setError(null);
  };

  // Create Stripe checkout session and redirect
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (amount < 100) {
      setError("Amount must be at least $1.00");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get authentication token if user is logged in
      const token = currentUser ? await currentUser.getIdToken() : null;

      const headers = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
        headers["Content-Type"] = "application/json";
      }

      // Store the referrer in the session storage so it persists after Stripe redirect
      sessionStorage.setItem("tipFormReferrer", referrer);

      // Call backend to create checkout session
      const response = await axios.post(
        "/api/payments/create-checkout-session",
        {
          amount,
          message,
          customerName,
          userId: currentUser?.uid || null,
          // Set success URL to redirect to membership page with session_id
          successUrl: `${
            window.location.origin
          }/membership?session_id={CHECKOUT_SESSION_ID}&referrer=${encodeURIComponent(
            referrer
          )}`,
          cancelUrl: `${
            window.location.origin
          }/tip?referrer=${encodeURIComponent(referrer)}`,
        },
        { headers }
      );

      if (response.data && response.data.sessionId) {
        console.log("Checkout session created:", response.data.sessionId);

        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error("Failed to create checkout session");
      }
    } catch (err) {
      console.error("Error creating checkout session:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Could not initiate payment process";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check for session_id in URL and verify payment if present
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const sessionId = queryParams.get("session_id");
    const urlReferrer = queryParams.get("referrer");

    console.log("URL search params:", location.search);
    console.log("Session ID from URL:", sessionId);

    // Update referrer if provided in URL
    if (urlReferrer) {
      setReferrer(urlReferrer);
    } else {
      // Try to get from sessionStorage as fallback
      const storedReferrer = sessionStorage.getItem("tipFormReferrer");
      if (storedReferrer) {
        setReferrer(storedReferrer);
      }
    }

    if (sessionId) {
      const verifyPayment = async () => {
        setLoading(true);
        try {
          const token = currentUser ? await currentUser.getIdToken() : null;

          const headers = {};
          if (token) {
            headers["Authorization"] = `Bearer ${token}`;
          }

          console.log("Verifying session:", sessionId);
          const response = await axios.get(
            `/api/payments/verify-session?session_id=${sessionId}`,
            { headers }
          );

          console.log("Verification response:", response.data);
          if (response.data && response.data.success) {
            console.log("Payment verification successful");

            // Redirect to membership page with success flag
            // This will be picked up by the membership page to show the success modal
            navigate("/membership", {
              replace: true,
              state: { showSuccessModal: true },
            });
          } else {
            console.log("Payment verification failed");
            setError("Payment verification failed. Please contact support.");
          }
        } catch (err) {
          console.error("Error verifying payment:", err);
          setError("Could not verify payment status. Please contact support.");
        } finally {
          setLoading(false);
        }
      };

      verifyPayment();
    }
  }, [currentUser, location.search, navigate]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        "& > :not(style)": { m: 1 },
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Amount Selection */}
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Select Tip Amount
        </Typography>
        <Grid container spacing={1}>
          {tipOptions.map((option) => (
            <Grid item xs={3} key={option.value}>
              <Button
                variant={amount === option.value ? "contained" : "outlined"}
                color="primary"
                fullWidth
                onClick={() => handlePredefinedAmount(option.value)}
              >
                {option.label}
              </Button>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Custom Amount ($)
          </Typography>
          <TextField
            label="Enter Amount"
            type="number"
            fullWidth
            variant="outlined"
            size="small"
            InputProps={{ inputProps: { min: 1, step: 0.01 } }}
            onChange={handleCustomAmountChange}
          />
        </Box>
      </Box>

      {/* Message Input (Optional) */}
      <TextField
        label="Leave a Message (Optional)"
        variant="outlined"
        fullWidth
        multiline
        rows={2}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Your message of support..."
      />

      {/* Name Input (Optional) */}
      <TextField
        label="Your Name (Optional)"
        variant="outlined"
        fullWidth
        value={customerName}
        onChange={(e) => setCustomerName(e.target.value)}
        placeholder="Your name or nickname..."
      />

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Submit Button */}
      <Button
        variant="contained"
        color="primary"
        fullWidth
        type="submit"
        disabled={loading || amount < 100}
        sx={{ py: 1.5, mt: 3 }}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          `Send $${(amount / 100).toFixed(2)} Tip`
        )}
      </Button>

      <Typography
        variant="caption"
        color="text.secondary"
        align="center"
        sx={{ mt: 2, display: "block" }}
      >
        You'll be securely redirected to Stripe to complete your payment.
      </Typography>
    </Box>
  );
};

export default TipForm;
